"use client";

import { useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

import type { FacetCounts } from "@/lib/public-hearings/apply-filters";
import { calgaryYmdString } from "@/lib/public-hearings/deadline";
import {
  EMPTY_FILTER_STATE,
  encodeFilterState,
  parseFilterState,
  type FilterState,
  type HearingStatus,
  type HearingType,
} from "@/lib/public-hearings/filter-state";
import { Chip } from "./Chip";
import { CommunityAutocomplete } from "./CommunityAutocomplete";
import { FilterGroup } from "./FilterGroup";
import { addDaysYmd, toggleNumeric, toggleStringMember } from "./helpers";

type Props = {
  // Full set of community names known to the page, derived from the
  // unfiltered upstream payload server-side. Drives the "Find near…"
  // typeahead so the vocabulary always reflects every community with
  // an upcoming hearing — even when the visible list is narrowed.
  communityVocab: string[];
  // Ward numbers that appear in the unfiltered payload, sorted. Used
  // to render the Ward chip group; non-existent wards aren't shown.
  wardVocab: number[];
  // Per-facet counts against the unfiltered set, used for the `(N)`
  // counts inside each chip. Computed against the unfiltered set
  // deliberately so the counts don't shift as the user toggles other
  // facets — see `facetCounts` doc.
  facetCounts: FacetCounts;
  // Total upcoming hearing count, for the "All" chip in the Ward group.
  totalCount: number;
  // Count after filters are applied — drives the "Showing x of y" label.
  filteredCount: number;
};

// Filter bar: chip groups for Ward / Status / Type, plus a search input
// and a single-select community typeahead. Custom hearing-date inputs
// live behind a "More" disclosure (rare). Not sticky — the bar sits in
// page flow above the results.
export function HearingsFilters({
  communityVocab,
  wardVocab,
  facetCounts,
  totalCount,
  filteredCount,
}: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();
  const [moreOpen, setMoreOpen] = useState(false);

  const state = useMemo<FilterState>(
    () => parseFilterState(searchParams),
    [searchParams],
  );

  const updateState = useCallback(
    (next: FilterState) => {
      const sp = encodeFilterState(next);
      const qs = sp.toString();
      const url = qs ? `?${qs}` : window.location.pathname;
      startTransition(() => {
        router.replace(url, { scroll: false });
      });
    },
    [router, startTransition],
  );

  // Debounced search: keep the input value in local state so typing
  // stays responsive, and push to the URL (which triggers a server
  // re-render via `router.replace`) only after the reader has paused.
  const [queryDraft, setQueryDraft] = useState(state.q);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPushedRef = useRef(state.q);
  useEffect(() => {
    if (state.q !== lastPushedRef.current) {
      lastPushedRef.current = state.q;
      setQueryDraft(state.q);
    }
  }, [state.q]);
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);
  const setQuery = (q: string) => {
    setQueryDraft(q);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      lastPushedRef.current = q;
      updateState({ ...state, q });
    }, 220);
  };

  const setCommunity = (community: string | null) =>
    updateState({ ...state, community });
  const setHearingDate = (kind: "after" | "before", value: string) =>
    updateState({
      ...state,
      [kind === "after" ? "hearingAfter" : "hearingBefore"]: value || null,
    });

  // Multi-select toggle helpers — pure, sort-stable so the URL doesn't
  // churn over reorderings.
  const toggleWard = (ward: number) =>
    updateState({
      ...state,
      wards: toggleNumeric(state.wards, ward),
    });
  const toggleStatus = (status: HearingStatus) =>
    updateState({
      ...state,
      statuses: toggleStringMember(state.statuses, status),
    });
  const toggleType = (type: HearingType) =>
    updateState({
      ...state,
      types: toggleStringMember(state.types, type),
    });
  const clearWards = () =>
    state.wards.length > 0 && updateState({ ...state, wards: [] });

  const setHearingWindow = (days: number) => {
    const todayYmd = calgaryYmdString(new Date());
    updateState({
      ...state,
      hearingAfter: todayYmd,
      hearingBefore: addDaysYmd(todayYmd, days),
    });
  };

  const isPresetActive = (days: number): boolean => {
    if (!state.hearingAfter || !state.hearingBefore) return false;
    const todayYmd = calgaryYmdString(new Date());
    return (
      state.hearingAfter === todayYmd &&
      state.hearingBefore === addDaysYmd(todayYmd, days)
    );
  };

  const togglePreset = (days: number) => {
    if (isPresetActive(days)) {
      updateState({ ...state, hearingAfter: null, hearingBefore: null });
    } else {
      setHearingWindow(days);
    }
  };

  const clearAll = () => {
    setMoreOpen(false);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setQueryDraft("");
    lastPushedRef.current = "";
    updateState(EMPTY_FILTER_STATE);
  };

  const activeCount =
    (state.q ? 1 : 0) +
    (state.community ? 1 : 0) +
    state.wards.length +
    state.statuses.length +
    state.types.length +
    (state.hearingAfter ? 1 : 0) +
    (state.hearingBefore ? 1 : 0);

  const customDatesActive =
    (state.hearingAfter || state.hearingBefore) &&
    !isPresetActive(8) &&
    !isPresetActive(30);

  return (
    <section
      aria-label="Filter upcoming hearings"
      className="ph-filters"
    >
      <FilterGroup label="Ward">
        <Chip
          pressed={state.wards.length === 0}
          onClick={clearWards}
          label="All"
          count={totalCount}
        />
        {wardVocab.map((w) => (
          <Chip
            key={`w-${w}`}
            pressed={state.wards.includes(w)}
            onClick={() => toggleWard(w)}
            label={`W${w}`}
            count={facetCounts.ward.get(w) ?? 0}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Submission status">
        <Chip
          pressed={state.statuses.includes("open")}
          onClick={() => toggleStatus("open")}
          label="Open"
          count={facetCounts.status.open}
        />
        <Chip
          pressed={state.statuses.includes("closing")}
          onClick={() => toggleStatus("closing")}
          label="Closing < 48h"
          count={facetCounts.status.closing}
        />
        <Chip
          pressed={state.statuses.includes("closed")}
          onClick={() => toggleStatus("closed")}
          label="Closed"
          count={facetCounts.status.closed}
        />
      </FilterGroup>

      <FilterGroup label="Type">
        <Chip
          pressed={state.types.includes("land_use")}
          onClick={() => toggleType("land_use")}
          label="Land use"
          count={facetCounts.type.land_use}
        />
        <Chip
          pressed={state.types.includes("outline_plan")}
          onClick={() => toggleType("outline_plan")}
          label="Outline plan"
          count={facetCounts.type.outline_plan}
        />
        <Chip
          pressed={state.types.includes("policy")}
          onClick={() => toggleType("policy")}
          label="Policy"
          count={facetCounts.type.policy}
        />
      </FilterGroup>

      <div className="ph-filter-group">
        <span className="ph-filter-glabel">Hearing date</span>
        <div className="ph-filter-chip-row">
          <Chip
            pressed={isPresetActive(8)}
            onClick={() => togglePreset(8)}
            label="Next 8 days"
          />
          <Chip
            pressed={isPresetActive(30)}
            onClick={() => togglePreset(30)}
            label="Next 30 days"
          />
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-expanded={moreOpen}
            className={`ph-filter-chip ${
              moreOpen || customDatesActive ? "ph-filter-chip-pressed" : ""
            }`}
          >
            Custom {moreOpen ? "▴" : "▾"}
          </button>
        </div>

        {moreOpen && (
          <div className="ph-filter-custom-dates">
            <span className="ph-filter-glabel">Custom date range</span>
            <label className="ph-filter-date-label">
              after
              <input
                type="date"
                value={state.hearingAfter ?? ""}
                onChange={(e) => setHearingDate("after", e.target.value)}
                className="ph-filter-date-input"
              />
            </label>
            <label className="ph-filter-date-label">
              before
              <input
                type="date"
                value={state.hearingBefore ?? ""}
                onChange={(e) => setHearingDate("before", e.target.value)}
                className="ph-filter-date-input"
              />
            </label>
          </div>
        )}
      </div>

      <div className="ph-filter-search">
        <span className="ph-filter-glabel">Community / address</span>
        <div className="ph-filter-search-row">
          <input
            type="search"
            value={queryDraft}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address, topic, bylaw…"
            aria-label="Search hearings"
            className="ph-filter-input"
          />
          <CommunityAutocomplete
            vocab={communityVocab}
            value={state.community}
            onChange={setCommunity}
          />
        </div>
      </div>

      <div className="ph-filter-footer">
        <p className="ph-filter-result-count">
          {filteredCount < totalCount
            ? `Showing ${filteredCount} of ${totalCount} hearings`
            : `${totalCount} upcoming hearing${totalCount === 1 ? "" : "s"}`}
          , grouped by hearing day.
        </p>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="ph-filter-clear"
          >
            clear {activeCount} filter{activeCount === 1 ? "" : "s"}
          </button>
        )}
      </div>

    </section>
  );
}
